/**
 * window.asGlobals: the runtime global. Debug hooks, plus the library's
 * helpers and element accessors for pages that load only the .js and can't
 * import the ES module.
 *
 * The object is created here and only here. Every contributor (store.ts,
 * paramInputRegistry.ts, projectManagement.ts, exports.ts, as-app.ts) calls
 * addAsGlobals() to merge its part in, so load order doesn't matter. The
 * interface below is the one place the full shape is written down.
 *
 * The `typeof import(...)` types are type-only, so they're erased at build
 * time and don't create runtime cycles with the modules they describe.
 */

export interface AsGlobals {
  /** The library's internal store (store.ts). */
  internalData: typeof import('./store/store.ts').internalData;
  /** The open project's appData store (data/projectManagement.ts). */
  currentProjectData: any;
  /** Which inputs are bound to which Paramate param (paramInputRegistry.ts). */
  paramateInputRegistry: typeof import('./store/paramInputRegistry.ts').paramateInputRegistry;
  /** The helper functions, the same object as the `AsUtils` ES export. */
  utils: typeof import('./exports.ts').AsUtils;
  // Singleton element accessors, same functions as the ES exports (util/singletons.ts).
  asApp: typeof import('./util/singletons.ts').asApp;
  asConfigurator: typeof import('./util/singletons.ts').asConfigurator;
  asHeader: typeof import('./util/singletons.ts').asHeader;
  requireAsApp: typeof import('./util/singletons.ts').requireAsApp;
  requireAsConfigurator: typeof import('./util/singletons.ts').requireAsConfigurator;
  requireAsHeader: typeof import('./util/singletons.ts').requireAsHeader;
  whenAsApp: typeof import('./util/singletons.ts').whenAsApp;
  whenAsConfigurator: typeof import('./util/singletons.ts').whenAsConfigurator;
  whenAsHeader: typeof import('./util/singletons.ts').whenAsHeader;
  whenConfigurator: typeof import('./util/singletons.ts').whenConfigurator;
  wcQry: typeof import('./util/wcUtil.ts').wcQry;
  wcQryAll: typeof import('./util/wcUtil.ts').wcQryAll;
  createElWithAttrs: typeof import('./util/wcUtil.ts').createElWithAttrs;
  replaceChildren: typeof import('./util/wcUtil.ts').replaceChildren;
  sendEv: typeof import('./data/events.ts').sendEv;
  // Debug hooks. Optional: they only exist once the component that owns them
  // is on the page. See docs/sessionsAndExpiry.md.
  triggerExpiry?: () => void;
  invalidateSession?: () => void;
  simulateSleep?: (minutes?: number) => void;
  forceSessionExpire?: () => void;
  setCanDownload?: (value?: boolean) => void;
}

declare global {
  interface Window {
    asGlobals: AsGlobals;
    /** Loose debug flags, set on the page rather than through asGlobals.
     *  `verbose` and `devlogTraces` drive devlog (util/utils.ts), `noAPI`
     *  suppresses external calls, `stopRedir` holds back the sign-in redirect,
     *  `fakeProduction` makes isProduction true. `verbose` has a setter on it,
     *  installed in main.ts. */
    verbose?: boolean;
    devlogTraces?: boolean;
    fakeProduction?: boolean;
    noAPI?: boolean;
    stopRedir?: boolean;
    testVec?: any;
  }
}

// Created empty, then filled in by the contributors. Cast rather than typed
// optional, so consumers don't need `?.` on properties the bundle always ends
// up having. An object made earlier (a test shim, say) is kept and merged into.
if (typeof window !== 'undefined') {
  window.asGlobals = (window.asGlobals ?? {}) as AsGlobals;
}

/** Merge one module's contribution into window.asGlobals. */
export function addAsGlobals(part: Partial<AsGlobals>): void {
  if (typeof window === 'undefined') return;
  Object.assign(window.asGlobals, part);
}
